import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, ArrowUpRight, Loader2, CalendarX2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { PresignedImage } from '../components/PresignedMedia';
import { timePageLoad } from '../utils/performanceLogger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const pageTimer = timePageLoad('EventsPage');

  useEffect(() => {
    fetchEvents();
  }, [tab]);

  useEffect(() => {
    if (!loading) pageTimer.markReady({ count: events.length });
  }, [loading]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = tab === 'upcoming' ? { upcoming_only: true, limit: 50 } : { upcoming_only: false, limit: 50 };
      const res = await axios.get(`${API}/events`, { params });
      let list = res.data.events || [];
      if (tab === 'past') {
        const now = new Date();
        list = list.filter(e => new Date(e.event_date) < now).sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
      }
      setEvents(list);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  const mapsUrl = (event) => {
    if (event.location_lat && event.location_lng) {
      return `https://www.google.com/maps/search/?api=1&query=${event.location_lat},${event.location_lng}`;
    }
    if (event.location_address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location_address)}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-4 sm:px-6 py-10" data-testid="events-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-10">
          <p className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2">RydQuest</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white">Upcoming Events</h1>
          <p className="text-neutral-400 mt-3 max-w-xl">
            Meets, cruises, shows, and takeovers. Tap an event to get directions.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-[#121212] rounded-xl mb-8 max-w-sm">
          {['upcoming', 'past'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`events-tab-${t}`}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all capitalize ${
                tab === t ? 'bg-red-500 text-white' : 'text-neutral-400 hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-500" /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <CalendarX2 className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No {tab} events</h3>
            <p className="text-neutral-400">Check back soon.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="events-grid">
            {events.map(event => {
              const directions = mapsUrl(event);
              return (
                <article
                  key={event.id}
                  className="group bg-[#121212] rounded-2xl border border-white/10 overflow-hidden hover:border-red-500/50 transition-all card-hover"
                  data-testid={`event-card-${event.id}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    {event.image_url ? (
                      <PresignedImage
                        src={event.image_url}
                        alt={event.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-500/20 to-purple-500/20 flex items-center justify-center">
                        <Calendar className="w-14 h-14 text-red-400/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                    <Badge className="absolute top-3 left-3 bg-black/60 text-white border-white/10 backdrop-blur-sm">
                      {formatDate(event.event_date)}
                      {event.event_time && ` · ${event.event_time}`}
                    </Badge>
                  </div>

                  <div className="p-5 space-y-3">
                    <h3 className="text-xl font-bold text-white">{event.name}</h3>
                    {event.description && (
                      <p className="text-sm text-neutral-400 line-clamp-3">{event.description}</p>
                    )}

                    {event.location_address && (
                      <a
                        href={directions}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-2 text-sm text-neutral-300 hover:text-red-400 transition-colors group/loc"
                        data-testid={`event-directions-${event.id}`}
                      >
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
                        <span className="underline-offset-2 group-hover/loc:underline">{event.location_address}</span>
                      </a>
                    )}

                    {event.social_link && (
                      <a
                        href={event.social_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 font-medium"
                        data-testid={`event-social-${event.id}`}
                      >
                        View post <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
